/**
 * Widget Configuration Types
 * Type definitions for provider widget configuration management
 */

export interface BrandingSettings {
  companyName: string;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor?: string;
  fontFamily?: string;
  heroImage?: string;
}

export interface ServiceOffering {
  containerSize: string; // '10Y', '20Y', '30Y', '40Y'
  description: string;
  available: boolean;
  capacity?: number;
}

export interface PricingSettings {
  basePricing: Record<string, number>; // { '10Y': 350, '20Y': 450, ... }
  deliveryFee?: number;
  disposalFees?: Record<string, number>; // Material type -> price per ton
  rentalPeriodDays: number;
  overageFeePerDay?: number;
  environmentalFee?: number;
  taxRate?: number; // As decimal (0.0825 for 8.25%)
}

export interface BusinessInformation {
  contactEmail: string;
  contactPhone: string;
  website?: string;
  businessAddress?: string;
  businessHours?: string;
  licenseNumber?: string;
}

export interface EmbedSettings {
  allowedDomains: string[];
  defaultWidth?: string; // '100%', '800px'
  defaultHeight?: string; // '800px'
  analyticsEnabled: boolean;
}

export interface WidgetConfiguration {
  id: string;
  providerId: string;
  slug: string;
  branding: BrandingSettings;
  services: ServiceOffering[];
  pricing: PricingSettings;
  businessInfo: BusinessInformation;
  embedSettings: EmbedSettings;
  updatedAt: string;
}

// Request DTOs
export interface UpdateBrandingDTO {
  companyName: string;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor?: string;
  fontFamily?: string;
  heroImage?: string;
}

export interface UpdateServicesDTO {
  services: ServiceOffering[];
}

export interface UpdatePricingDTO {
  basePricing: Record<string, number>;
  deliveryFee?: number;
  disposalFees?: Record<string, number>;
  rentalPeriodDays: number;
  overageFeePerDay?: number;
  environmentalFee?: number;
  taxRate?: number;
}

export interface UpdateBusinessInfoDTO {
  contactEmail: string;
  contactPhone: string;
  website?: string;
  businessAddress?: string;
  businessHours?: string;
  licenseNumber?: string;
}

export interface UpdateEmbedSettingsDTO {
  allowedDomains: string[];
  defaultWidth?: string;
  defaultHeight?: string;
  analyticsEnabled: boolean;
}

// Response types
export interface WidgetConfigResponse {
  config: WidgetConfiguration;
}

export interface EmbedCodeResponse {
  embedCode: string;
  widgetUrl: string;
  previewUrl: string;
}

export interface LogoUploadResponse {
  success: boolean;
  logoUrl: string;
}

// Container sizes enum
export const CONTAINER_SIZES = ['10Y', '20Y', '30Y', '40Y'] as const;
export type ContainerSize = typeof CONTAINER_SIZES[number];

// Font options
export const FONT_FAMILIES = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Source Sans Pro',
  'Raleway',
  'PT Sans',
] as const;
export type FontFamily = typeof FONT_FAMILIES[number];

// Material types for disposal fees
export const MATERIAL_TYPES = [
  'mixed_cd',
  'concrete',
  'dirt',
  'green_waste',
  'clean_wood',
  'asphalt',
  'roofing',
  'brick',
] as const;
export type MaterialType = typeof MATERIAL_TYPES[number];

export const MATERIAL_TYPE_LABELS: Record<MaterialType, string> = {
  mixed_cd: 'Mixed C&D',
  concrete: 'Concrete',
  dirt: 'Dirt/Soil',
  green_waste: 'Green Waste',
  clean_wood: 'Clean Wood',
  asphalt: 'Asphalt',
  roofing: 'Roofing Materials',
  brick: 'Brick/Block',
};
